import { QueryLazyOptions } from "@apollo/client";
import Autocomplete from "@saleor/components/AutocompleteForObject";
import MyTypography from "@saleor/components/MyTypography";
import { Exact } from "@saleor/graphql";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import uniqBy from "lodash/uniqBy";
import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

interface IProps {
  handleSelectedSearch?: (value: any) => void;
  value?: string;
  setValue?: React.Dispatch<React.SetStateAction<string>>;
  placeholder?: string;
  first?: number;
  isClean?: boolean;
  contained: boolean;
  isWidthAuto?: boolean;
  isSearchIcon?: boolean;
  name?: string;
  totalCount?: number;
  getDataSuggestions: (
    options?: QueryLazyOptions<
      Exact<{
        saleOrg?: any;
        distributionChannel: any;
        division: any;
        filter?: any;
        first?: number;
      }>
    >
  ) => void;
  fetchDataSuggestions?: any;
  loadingDataSuggestion: boolean;
  dataOptions: any[];
  error?: boolean;
  searchField: string;
  objectSuggestion?: boolean;
  defaultInputValue?: string;
  defaultValue?: any;
  withNonSelect?: boolean;
  inputValue?: boolean;
  disabled?: boolean;
  pageInfo?: any;
  showLoadMore?: boolean;
  disableDuplicationLoading?: boolean;
  isCheckingRole?: boolean;
  isDomestic?: boolean;
  salesCode?: any;
  handleClear?: any;
  saleOrgEmpty?: boolean;
  isChangeFlow?: boolean;
  id?: string;
  onMouseLeave?(event: any);
  onClear?(event?: any);
}

interface LastItemParamsProps {
  id: string;
  position: number;
}

enum MessageMode {
  WAITING = "WAITING",
  SOLD_TO_NOT_FOUND = "SOLD_TO_NOT_FOUND",
  SOLD_TO_EXISTS = "SOLD_TO_EXISTS",
  CUSTOMER_BLOCK = "CUSTOMER_BLOCK",
  CREDIT_BLOCK = "CREDIT_BLOCK"
}

const SoldToInputSuggestion: React.FC<IProps> = (props: IProps) => {
  const {
    handleSelectedSearch,
    placeholder,
    first,
    isClean,
    contained,
    isWidthAuto,
    isSearchIcon,
    totalCount,
    getDataSuggestions,
    loadingDataSuggestion,
    dataOptions,
    error,
    searchField,
    objectSuggestion,
    onMouseLeave,
    defaultInputValue,
    defaultValue,
    withNonSelect,
    disabled,
    pageInfo,
    showLoadMore,
    disableDuplicationLoading,
    isDomestic,
    salesCode,
    handleClear,
    saleOrgEmpty,
    isChangeFlow,
    id
  } = props;

  const firstRender = useRef(true);
  const [value, setValue] = useState<string>("");
  const [opts, setOpts] = useState<any[]>([]);
  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(100);
  const paginateValues = useLocalPaginator(setValuesPaginationState);
  const { loadNextPage } = paginateValues(pageInfo, valuesPaginationState);
  const [lastItemParams, setLastItemParams] = useState<LastItemParamsProps>(
    null
  );
  const intl = useIntl();
  const [mode, setMode] = useState<MessageMode>(MessageMode.WAITING);

  useEffect(() => {
    if (firstRender.current && !value && defaultInputValue && defaultValue) {
      setValue(defaultInputValue);
      firstRender.current = false;
    }
  }, [defaultInputValue, defaultValue, firstRender.current]);

  const handleChange = event => {
    setValue(event);
  };

  const onLastItemVisible = (id: string, position: number) => {
    setLastItemParams({ id, position });
    loadNextPage();
  };

  useEffect(() => {
    if (isDomestic && value?.length >= 2 && !isChangeFlow) {
      const filterObj = { [searchField]: value };
      getDataSuggestions({
        variables: {
          first: first || 100,
          saleOrg: salesCode?.salesOrganizationCode,
          distributionChannel: salesCode?.distributionChannelCode,
          division: salesCode?.divisionCode,
          filter: filterObj
        }
      });
    }
  }, [value]);

  useEffect(() => {
    if (loadingDataSuggestion) {
      setMode(MessageMode.SOLD_TO_EXISTS);
      return;
    }
  }, [loadingDataSuggestion]);

  useEffect(() => {
    if (!disableDuplicationLoading) {
      const uniqOpts = uniqBy([...opts, ...dataOptions], "value");
      setOpts(uniqOpts);
    } else {
      setOpts(dataOptions);
    }

    if (lastItemParams?.id) {
      setTimeout(() => {
        const { id: lastItemId, position } = lastItemParams;
        const el = document.getElementById(lastItemId);
        if (el) {
          el.scrollTo({ top: position });
        }
      }, 100);
    }
  }, [dataOptions]);

  useEffect(() => {
    setLastItemParams(null);
    handleClear(value);
    if (value === "") {
      setMode(MessageMode.WAITING);
      setOpts([]);
    }
  }, [value]);

  return (
    <>
      <div>
        <Autocomplete
          contained={contained}
          isWidthAuto={isWidthAuto}
          isSearchIcon={isSearchIcon}
          defaultInputValue={defaultInputValue}
          optionsProp={opts}
          placeholderProps={placeholder}
          totalCount={totalCount || opts.length}
          onLastItemVisible={showLoadMore && onLastItemVisible}
          onChangeProps={e => {
            handleChange(e);
            if (withNonSelect) {
              handleSelectedSearch(e);
            }
          }}
          loading={loadingDataSuggestion}
          onSearch={handleSelectedSearch}
          objectSuggestion={objectSuggestion}
          isReset={isClean}
          defaultValue={defaultValue}
          onMouseLeave={onMouseLeave}
          error={error}
          disabled={disabled}
          id={id}
          testId={id}
        />
      </div>
      {mode === MessageMode.WAITING && !saleOrgEmpty && !isChangeFlow && (
        <div style={{ marginTop: "10px" }}>
          <MyTypography variant="body3">
            {intl.formatMessage({
              defaultMessage: "พิมพ์ค้นหาและเลือก Sold-to เพื่อสร้างคำสั่งซื้อ"
            })}
          </MyTypography>
        </div>
      )}
    </>
  );
};

export default SoldToInputSuggestion;
